/**
 * TemplatePicker — Selettore template messaggi con risoluzione variabili
 */

import { useState, useEffect, useRef } from 'react';
import { FileText, Search, Plus, Trash2, ChevronRight, Loader2 } from 'lucide-react';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useApp } from '@/components/AppProvider';
import { iconTip } from '@/components/messages/ChatTooltip';

interface Props {
  onInsert: (text: string) => void;
  onClose: () => void;
  /** Valori gia' noti (es. {{nome}}, {{azienda}} dal Lead/Cliente della chat). */
  initialValues?: Record<string, string | undefined>;
}

function extractVariables(content: string): string[] {
  const matches = content.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, '')))];
}

function resolveVariables(content: string, values: Record<string, string>): string {
  return content.replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] || `{{${key}}}`);
}

export function TemplatePicker({ onInsert, onClose, initialValues }: Props) {
  const { confirm } = useApp();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { templates, templatesLoading, fetchTemplates, createTemplate, deleteTemplate } = useWhatsAppChatMetaStore();
  const { wahaConfig } = useWhatsAppChatStore();
  const orgId = wahaConfig?.organizationId;

  useEffect(() => {
    if (orgId && templates.length === 0) fetchTemplates(orgId);
  }, [orgId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const filtered = templates.filter((t) =>
    t.name.toLowerCase().includes(query.toLowerCase()) ||
    t.content.toLowerCase().includes(query.toLowerCase())
  );

  const selectedTemplate = templates.find((t) => t.id === selected);
  const variables = selectedTemplate ? extractVariables(selectedTemplate.content) : [];

  const handleInsert = () => {
    if (!selectedTemplate) return;
    const resolved = resolveVariables(selectedTemplate.content, varValues);
    onInsert(resolved);
    onClose();
  };

  const handleCreate = async () => {
    if (!orgId || !newName.trim() || !newContent.trim()) return;
    setSaving(true);
    await createTemplate(orgId, newName.trim(), newContent.trim(), newCategory || undefined);
    setNewName(''); setNewContent(''); setNewCategory('');
    setCreating(false);
    setSaving(false);
  };

  return (
    <div
      ref={ref}
      className="absolute z-50 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 w-80"
      style={{ bottom: '100%', left: 0, marginBottom: '4px' }}
    >
      {selected && selectedTemplate ? (
        /* Variabili */
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => setSelected(null)} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
              <ChevronRight className="w-4 h-4 text-[#787774] rotate-180" />
            </button>
            <h4 className="text-sm font-semibold text-[#37352F] dark:text-white truncate">{selectedTemplate.name}</h4>
          </div>
          <div className="text-xs text-[#787774] bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg px-3 py-2 mb-3 line-clamp-3">
            {resolveVariables(selectedTemplate.content, varValues)}
          </div>
          {variables.length > 0 && (
            <div className="space-y-2 mb-3">
              <p className="text-[10px] font-semibold text-[#787774] uppercase tracking-wide">Variabili</p>
              {variables.map((v) => (
                <div key={v}>
                  <label className="text-xs text-[#787774] mb-0.5 block">{`{{${v}}}`}</label>
                  <input
                    value={varValues[v] || ''}
                    onChange={(e) => setVarValues((prev) => ({ ...prev, [v]: e.target.value }))}
                    placeholder={`Valore per ${v}...`}
                    className="w-full text-xs bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded px-2 py-1.5 outline-none focus:border-[#2383E2] text-[#37352F] dark:text-white"
                  />
                </div>
              ))}
            </div>
          )}
          <button onClick={handleInsert} className="w-full py-2 text-sm font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] rounded-lg transition-colors">
            Inserisci nel messaggio
          </button>
        </div>
      ) : creating ? (
        /* Crea template */
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setCreating(false)} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
              <ChevronRight className="w-4 h-4 text-[#787774] rotate-180" />
            </button>
            <h4 className="text-sm font-semibold text-[#37352F] dark:text-white">Nuovo template</h4>
          </div>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome template *"
            className="w-full text-xs bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded px-2 py-2 outline-none focus:border-[#2383E2] text-[#37352F] dark:text-white" />
          <textarea value={newContent} onChange={(e) => setNewContent(e.target.value)} placeholder="Testo del template. Usa {{nome}}, {{azienda}} per variabili *" rows={4}
            className="w-full text-xs resize-none bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded px-2 py-2 outline-none focus:border-[#2383E2] text-[#37352F] dark:text-white" />
          <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Categoria (opzionale)"
            className="w-full text-xs bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded px-2 py-2 outline-none focus:border-[#2383E2] text-[#37352F] dark:text-white" />
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={!newName.trim() || !newContent.trim() || saving}
              className="flex-1 py-2 text-xs font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 rounded-lg flex items-center justify-center gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salva'}
            </button>
            <button onClick={() => setCreating(false)} className="flex-1 py-2 text-xs border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg text-[#787774] hover:bg-[rgba(55,53,47,0.04)]">
              Annulla
            </button>
          </div>
        </div>
      ) : (
        /* Lista template */
        <>
          <div className="p-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/8">
            <div className="flex items-center gap-2 bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg px-2 py-1.5">
              <Search className="w-3.5 h-3.5 text-[#787774]" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca template..."
                className="flex-1 text-xs bg-transparent outline-none text-[#37352F] dark:text-white placeholder:text-[#787774]" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {templatesLoading && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#787774]" /></div>}
            {!templatesLoading && filtered.length === 0 && (
              <p className="text-xs text-[#787774] text-center py-4">Nessun template trovato</p>
            )}
            {filtered.map((t) => (
              <div key={t.id} className="flex items-center hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 group">
                <button onClick={() => {
                  setSelected(t.id);
                  // Precompila le variabili note (maiuscole ignorate): restano modificabili.
                  const known: Record<string, string> = {};
                  for (const v of extractVariables(t.content)) {
                    const val = initialValues?.[v.toLowerCase()];
                    if (val) known[v] = val;
                  }
                  setVarValues(known);
                }}
                  className="flex-1 flex items-start gap-2.5 px-3 py-2.5 text-left min-w-0">
                  <FileText className="w-4 h-4 text-[#2383E2] flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#37352F] dark:text-white truncate">{t.name}</p>
                    {t.category && <p className="text-[10px] text-[#787774]">{t.category}</p>}
                    <p className="text-[11px] text-[#787774] truncate">{t.content.slice(0, 60)}{t.content.length > 60 ? '...' : ''}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-[#787774] flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100" />
                </button>
                <button onClick={async () => {
                    if (!orgId) return;
                    const ok = await confirm({
                      type: 'danger',
                      title: 'Eliminare il modello?',
                      message: `Il modello "${t.name}" sparisce per tutta l'organizzazione. Non si puo' annullare.`,
                      confirmText: 'Elimina',
                      cancelText: 'Annulla',
                    });
                    if (ok) deleteTemplate(t.id);
                  }}
                  className="px-2 py-2 text-[#787774] hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all"
                  {...iconTip('Elimina il modello')}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-[rgba(55,53,47,0.06)] dark:border-white/8">
            <button onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 text-xs text-[#2383E2] hover:text-[#1a6fc2] transition-colors">
              <Plus className="w-3.5 h-3.5" />
              Crea template
            </button>
          </div>
        </>
      )}
    </div>
  );
}
